import { useState, useEffect, useCallback } from 'react';
import api from '../api';
import { useToast } from '../context/ToastContext';

/**
 * Hook to manage user data fetching, pagination, and statistics.
 * @returns {Object} User management state and controls
 */
export const useUserManagement = () => {
  const { error: toastError } = useToast();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0
  });
  const [stats, setStats] = useState({});

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    setPagination((prev) => (prev.currentPage === 1 ? prev : { ...prev, currentPage: 1 }));
  }, [debouncedSearch]);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/admin/users', {
        params: {
          page: pagination.currentPage,
          limit: 50,
          search: debouncedSearch
        }
      });

      if (response.data.success) {
        setUsers(response.data.data.users);
        setPagination(prev => ({
          ...prev,
          totalPages: response.data.data.totalPages,
          totalCount: response.data.data.totalCount
        }));
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      toastError(error.response?.data?.message || 'Failed to fetch users');
    } finally {
      setLoading(false);
    }
  }, [pagination.currentPage, debouncedSearch, toastError]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await api.get('/api/admin/users/stats');
      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, []);

  const handleFilterClick = (type) => {
    if (searchTerm.includes(`filter:${type}`)) {
      setSearchTerm('');
    } else {
      setSearchTerm(`filter:${type}`);
    }
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, currentPage: newPage }));
  };

  const refreshData = useCallback(() => {
    fetchUsers();
    fetchStats();
  }, [fetchUsers, fetchStats]);

  useEffect(() => {
    refreshData();
    const interval = setInterval(refreshData, 60000); // Auto-refresh every 60s
    return () => clearInterval(interval);
  }, [refreshData]);

  return {
    users,
    loading,
    searchTerm,
    setSearchTerm,
    pagination,
    stats,
    handleFilterClick,
    handlePageChange,
    refreshData,
    setUsers // Exported for optimistic updates if needed
  };
};
